import { onBeforeUnmount, watch, type Ref } from 'vue'
import { isSlide } from '@collection-player/utils/slide-type.util'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import type { CollectionSlide } from '@collection-player/types/collection-player.type'
import type { Api } from '@collection-player/stores/app.store'
import { isEqual } from 'lodash-es'

export default ({
  nodeId,
  isSwiperVisible,
  activeSlide,
  api: { onFileEnter, onFileExit, onFilePageEnter, onFilePageExit },
}: {
  nodeId: string | undefined
  isSwiperVisible: Ref<boolean>
  activeSlide: Ref<CollectionSlide | null>
  api: Api
}) => {
  const node = { id: nodeId, type: ComponentTypes.CollectionPlayer }

  if (!onFileEnter || !onFileExit || !onFilePageEnter || !onFilePageExit) {
    console.warn('Tracking events for Collection Player are not provided.')
    return
  }

  const isSlideContinuation = (oldSlide?: CollectionSlide, newSlide?: CollectionSlide) =>
    oldSlide?.file.id === newSlide?.file.id && isSlide(oldSlide) && isSlide(newSlide)

  const sendEnterEvents = (slide?: CollectionSlide | null) => {
    if (!slide) return

    onFileEnter?.(node, slide.file)

    if (isSlide(slide)) {
      onFilePageEnter?.(node, slide.file, slide.slide.index)
    }
  }

  const sendExitEvents = (slide?: CollectionSlide | null) => {
    if (!slide) return

    if (isSlide(slide)) {
      onFilePageExit?.(node, slide.file, slide.slide.index)
    }

    onFileExit?.(node, slide.file)
  }

  const sendContinueEvents = (newSlide?: CollectionSlide | null, oldSlide?: CollectionSlide | null) => {
    const isFileContinuation = isSlideContinuation(oldSlide ?? undefined, newSlide ?? undefined)

    if (oldSlide) {
      if (isSlide(oldSlide)) {
        onFilePageExit?.(node, oldSlide.file, oldSlide.slide.index)
      }

      if (!isFileContinuation) {
        onFileExit?.(node, oldSlide.file)
      }
    }

    if (newSlide) {
      if (!isFileContinuation) {
        onFileEnter?.(node, newSlide.file)
      }

      if (isSlide(newSlide)) {
        onFilePageEnter?.(node, newSlide.file, newSlide.slide.index)
      }
    }
  }

  watch(
    [isSwiperVisible, activeSlide],
    ([newVisible, newSlide], [oldVisible, oldSlide]) => {
      // check by content not by reference
      if (isEqual(newSlide, oldSlide) && newVisible === oldVisible) {
        return
      }

      if (newVisible && !oldVisible) {
        sendEnterEvents(newSlide)
      } else if (!newVisible && oldVisible) {
        sendExitEvents(newSlide)
      } else if (newVisible) {
        sendContinueEvents(newSlide, oldSlide)
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    if (activeSlide.value && isSwiperVisible.value) {
      sendExitEvents(activeSlide.value)
    }
  })
}
